import {ContentStudioErrorResponse} from '@/types/backendError';
import contentStudioStore, {
  contentStudioActions,
  SelectedAsset,
  ErrorKind,
} from '@/stores/contentStudioStore';
import translate from './translate';

const ERROR_CODE_LOCALE_KEYS: Record<
  string,
  {message: string; kind: ErrorKind}
> = {
  INVALID_MEDIA_TYPE_ERROR: {
    message: 'The file must be a video',
    kind: ErrorKind.VALIDATION,
  },
  ASSET_WITHOUT_AUDIO_ERROR: {
    message: 'The file does not have an audio track',
    kind: ErrorKind.VALIDATION,
  },
  INVALID_RESOLUTION_ERROR: {
    message: 'The resolution of the provided file is not allowed',
    kind: ErrorKind.VALIDATION,
  },
  ASSET_NOT_READY_ERROR: {
    message: 'The file is not ready',
    kind: ErrorKind.VALIDATION,
  },
  ASSET_IN_TARGET_ASPECT_RATIO_ERROR: {
    message: 'The aspect ratio of the provided file is not allowed',
    kind: ErrorKind.VALIDATION,
  },
  ASSET_IN_TARGET_LANGUAGE_ERROR: {
    message: 'The language of the provided file is not allowed',
    kind: ErrorKind.VALIDATION,
  },
  FILE_TOO_LONG_ERROR: {
    message: 'Your video is too long, please upload files up to 3 mins',
    kind: ErrorKind.VALIDATION,
  },
  FILE_TOO_LARGE_ERROR: {
    message: 'Your video is too large, please upload files up to 1 GB',
    kind: ErrorKind.VALIDATION,
  },
  FILE_MEDIA_TYPE_ERROR: {
    message: 'The file must be a video',
    kind: ErrorKind.VALIDATION,
  },
  NO_REFORMATS_ERROR: {
    message: 'No reformats are available for the file',
    kind: ErrorKind.VALIDATION,
  },
};

const DEFAULT_FALLBACK_ERROR_LOCALE_KEY = 'An unexpected error occurred';

interface AxiosLikeError {
  response?: {
    data?: any;
    status?: number;
  };
}

export function raiseContentStudioError(
  errorMessage: string,
  errorKind: ErrorKind
): void {
  contentStudioActions.addError({
    title: 'Error',
    description: errorMessage,
    kind: errorKind,
  });
}

export function handleContentStudioError(
  error: unknown,
  fallbackErrorKind: ErrorKind = ErrorKind.FAILURE,
  fallbackError: string = DEFAULT_FALLBACK_ERROR_LOCALE_KEY
): void {
  // If error is a string, use it directly
  if (typeof error === 'string') {
    raiseContentStudioError(translate(error), fallbackErrorKind);
    return;
  }

  const selectedAssetsMap = contentStudioStore
    .getState()
    .selectedAssets.reduce((acc: {[id: string]: SelectedAsset}, asset) => {
      acc[asset.id] = asset;
      return acc;
    }, {});

  // Handle axios-like error with response.data
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as AxiosLikeError;
    if (axiosError.response?.data?.detail) {
      const {detail} = axiosError.response.data as ContentStudioErrorResponse;

      // Handle array of errors
      if (Array.isArray(detail)) {
        detail.forEach((errorDetail) => {
          if (errorDetail.code && errorDetail.asset_id) {
            const errorTuple = ERROR_CODE_LOCALE_KEYS[errorDetail.code];
            const errorMessage = errorTuple?.message;
            const errorKind = errorTuple?.kind || fallbackErrorKind;
            if (errorMessage) {
              const selectedAsset = selectedAssetsMap[errorDetail.asset_id];
              raiseContentStudioError(
                translate(errorMessage, {
                  assetName: selectedAsset?.fileName || '',
                }),
                errorKind
              );
            }
          }
        });
        return;
      }

      // Handle single error
      if (detail.code && detail.asset_id) {
        const errorTuple = ERROR_CODE_LOCALE_KEYS[detail.code];
        const errorMessage = errorTuple?.message || fallbackError;
        const errorKind = errorTuple?.kind || fallbackErrorKind;
        const selectedAsset = selectedAssetsMap[detail.asset_id];
        raiseContentStudioError(
          translate(errorMessage, {
            assetName: selectedAsset?.fileName || '',
          }),
          errorKind
        );
        return;
      }
    }
  }

  // For any other case, use fallback error
  raiseContentStudioError(translate(fallbackError), fallbackErrorKind);
}
